import React, { useMemo, useState } from 'react';
import axios from 'axios';
import './Auth.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useView } from '../../../context/ViewContext';
import { handleAuthSuccess } from '../../../utils/auth';
import { getPostAuthRedirectPath } from '../../../utils/authRedirect';
import { DEFAULT_DASHBOARD_PATH } from '../../../utils/workspaceAuthorization';
import {
  calculateOnboardingProgress,
  fetchOnboardingSteps,
} from '../../../utils/proOnboarding';
import Footer from '../../Footer';
import PasswordField from './PasswordField';
import { resolveInitialActiveBusiness } from '../../../utils/businessContext';
import { hasTeamModeAccess } from '../../../utils/teamModeAccess';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reactivation, setReactivation] = useState(null);

  const getLoginErrorMessage = (rawMessage) => {
    const normalized = rawMessage?.toString()?.toLowerCase() || '';

    if (!rawMessage) {
      return 'Unable to log in with those credentials. Please try again.';
    }

    if (normalized.includes('wrong password') || normalized.includes('incorrect password')) {
      return 'Incorrect password. Please double-check and try again.';
    }

    if (normalized.includes('no user') || normalized.includes('user not found')) {
      return 'No account was found for that email address.';
    }

    return rawMessage;
  };

  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentView } = useView();

  const successNotice = useMemo(() => location.state?.successNotice, [location.state]);

  const completeSignIn = async ({ token, user }) => {
    if (!token) {
      throw new Error('No token returned from login');
    }

      const authPayload = handleAuthSuccess({
        token,
        navigate,
        setCurrentView,
        navigateOnSuccess: false,
        user,
      });
      let destination = getPostAuthRedirectPath({
        role: authPayload?.role,
        platformRole: authPayload?.platformRole,
        user,
      });
      const nextPath = new URLSearchParams(location.search || '').get('next');

      if (authPayload?.role === 'professional' && !user?.workspaceLandingPath) {
        const activeBusiness = resolveInitialActiveBusiness(user);
        const activeBusinessId = activeBusiness?._id || null;
        const memberships = user?.businessMemberships || user?.memberships || [];
        const activeMembership = memberships.find((membership) =>
          String(membership.businessId || membership.business) === String(activeBusinessId || '')
        );
        const teamMode = hasTeamModeAccess(activeBusiness);
        const onboardingPath = teamMode
          ? '/onboarding/team'
          : '/onboarding';
        destination = onboardingPath;

        try {
          const onboardingSteps = await fetchOnboardingSteps(authPayload?._id, {
            activeBusinessId: teamMode ? activeBusinessId : null,
            teamMode,
            businessRole: activeMembership?.role || null,
          });
          const { isComplete } = calculateOnboardingProgress(onboardingSteps);
          destination = isComplete ? DEFAULT_DASHBOARD_PATH : onboardingPath;
        } catch (onboardingError) {
          console.error('Unable to check onboarding progress after login:', onboardingError);
        }
      }

      if (nextPath) {
        destination = getPostAuthRedirectPath({
          role: authPayload?.role,
          platformRole: authPayload?.platformRole,
          nextPath,
          user,
        }) || destination;
      }

      navigate(destination);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      setReactivation(null);
      const response = await axios.post('/api/auth/signin', {
        email,
        password,
        keepSignedIn,
      });
      const { token, error, user } = response.data || {};

      if (error) {
        setErrorMessage(getLoginErrorMessage(error));
        return;
      }

      await completeSignIn({ token, user });
    } catch (error) {
      // The password was right but the account is deactivated: offer it back rather
      // than failing the sign-in outright.
      const data = error?.response?.data;
      if (error?.response?.status === 403 && data?.code === 'ACCOUNT_DEACTIVATED') {
        setReactivation({
          token: data.reactivationToken,
          deactivatedAt: data.deactivatedAt || null,
        });
        setErrorMessage('');
        return;
      }

      console.error('Error logging in:', error);
      const friendlyMessage =
        data?.error ||
        data?.message ||
        error?.message;
      setErrorMessage(getLoginErrorMessage(friendlyMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      const response = await axios.post('/api/user/reactivate-account', {
        reactivationToken: reactivation?.token,
        keepSignedIn,
      });
      const { token, user, requiresResubscribe } = response.data || {};
      setReactivation(null);
      if (requiresResubscribe) {
        handleAuthSuccess({ token, navigate, setCurrentView, navigateOnSuccess: false, user });
        navigate('/signup/checkout');
        return;
      }
      await completeSignIn({ token, user });
    } catch (error) {
      console.error('Error reactivating account:', error);
      setErrorMessage(
        error?.response?.data?.error ||
          'We could not reactivate this account. Try signing in again.',
      );
      setReactivation(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="auth-page auth-page--narrow">
        <span className="deco deco--2 auth-deco--tl" aria-hidden="true" />
        <span className="deco deco--1 auth-deco--br" aria-hidden="true" />
        <div className="auth-inner auth-inner--narrow">
          <header className="auth-header">
            <span className="eyebrow">Welcome back</span>
            <h1>Log in to Haastia</h1>
            <p className="sub">Your calendar, your clients, your money.</p>
          </header>

          <section className="auth-form">
            <form className="auth-card" onSubmit={handleSubmit}>
              {reactivation && (
                <div className="auth-reactivate" role="status">
                  <strong>This account is deactivated</strong>
                  <p>
                    Nothing was deleted. Reactivate it and your profile, photos,
                    services and availability come back exactly as you left them.
                  </p>
                  <div className="auth-reactivate__actions">
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={handleReactivate}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Reactivating…' : 'Reactivate my account'}
                    </button>
                    <button
                      type="button"
                      className="auth-link subtle"
                      onClick={() => setReactivation(null)}
                      disabled={isSubmitting}
                    >
                      Not now
                    </button>
                  </div>
                </div>
              )}
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="login-email">Email address</label>
                  <div className="input-shell">
                    <input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isSubmitting}
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="login-password">Password</label>
                  <PasswordField
                    id="login-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isSubmitting}
                    autoComplete="current-password"
                    required
                  />
                  <div className="field-actions">
                    <Link to="/password/forgot" className="auth-link subtle">
                      Forgot password?
                    </Link>
                  </div>
                </div>

                <label className="checkbox-card" htmlFor="login-keep-signed-in">
                  <input
                    id="login-keep-signed-in"
                    type="checkbox"
                    checked={keepSignedIn}
                    onChange={(event) => setKeepSignedIn(event.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span>Keep me signed in for 30 days on this device</span>
                </label>
              </div>

              {successNotice && <p className="helper-text success">{successNotice}</p>}
              {errorMessage && <p className="helper-text error">{errorMessage}</p>}

              <button className="auth-submit" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Logging in...' : 'Log in'}
              </button>
              <p className="form-footer-note">
                New here?{' '}
                <Link className="auth-link" to="/signup">
                  Create an account
                </Link>
                .
              </p>
            </form>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Login;
